import { z } from "zod";
import { timeSchema } from "./schemas.js";

/**
 * What the onboarding questionnaire collects. It personalizes the assistant (tone, pace)
 * and gives every plan the context a person would otherwise have to repeat.
 * Option values are stored in Spanish because they go straight into the AI prompt.
 */
export const SITUATIONS = ["Mamá/papá en casa", "Trabajo fuera de casa", "Trabajo desde casa", "Estudio", "Otra"] as const;
export const FOCUS_AREAS = [
  "Salud y ejercicio",
  "Alimentación",
  "Orden y hogar",
  "Aprender algo nuevo",
  "Finanzas",
  "Descanso y calma",
  "Fe y espiritualidad",
  "Relaciones y familia",
  "Creatividad",
  "Trabajo o negocio",
] as const;
export const FREE_SLOTS = ["Temprano en la mañana", "Media mañana", "Mediodía", "Tarde", "Noche"] as const;
export const OBSTACLES = [
  "Me olvido",
  "No me alcanza el tiempo",
  "Empiezo con demasiado",
  "Me canso o me desanimo",
  "Imprevistos con la familia",
  "Me aburro",
] as const;
export const TONES = ["Cálido y paciente", "Directo y breve", "Con mucha energía"] as const;
export const PACES = ["Muy suave", "Moderado", "Ambicioso"] as const;

const text = (max: number) => z.string().trim().max(max).default("");

export const profileSchema = z.object({
  situation: z.enum(SITUATIONS).nullable().default(null),
  household: text(300),
  wakeTime: timeSchema.nullable().default(null),
  sleepTime: timeSchema.nullable().default(null),
  busyTimes: text(500),
  freeSlots: z.array(z.enum(FREE_SLOTS)).default([]),
  focusAreas: z.array(z.enum(FOCUS_AREAS)).default([]),
  wish: text(1000),
  obstacles: z.array(z.enum(OBSTACLES)).default([]),
  tone: z.enum(TONES).nullable().default(null),
  pace: z.enum(PACES).nullable().default(null),
  notes: text(1000),
});
export type Profile = z.infer<typeof profileSchema>;

export const emptyProfile: Profile = profileSchema.parse({});

/** Prompt-ready description of the person, in Spanish. Empty answers are left out. */
export function describeProfile(name: string, p: Profile): string {
  const lines = [`Nombre: ${name}`];
  if (p.situation) lines.push(`Situación: ${p.situation}`);
  if (p.household) lines.push(`Con quién vive / a quién cuida: ${p.household}`);
  if (p.wakeTime || p.sleepTime) lines.push(`Se levanta ${p.wakeTime ?? "?"} y se duerme ${p.sleepTime ?? "?"}`);
  if (p.busyTimes) lines.push(`Horarios ocupados: ${p.busyTimes}`);
  if (p.freeSlots.length) lines.push(`Momentos libres: ${p.freeSlots.join(", ")}`);
  if (p.focusAreas.length) lines.push(`Áreas que quiere mejorar: ${p.focusAreas.join(", ")}`);
  if (p.wish) lines.push(`Lo que quiere lograr o cambiar: ${p.wish}`);
  if (p.obstacles.length) lines.push(`Lo que antes le hizo dejar hábitos: ${p.obstacles.join(", ")}`);
  if (p.tone) lines.push(`Cómo prefiere que le hablen: ${p.tone}`);
  if (p.pace) lines.push(`Ritmo que prefiere: ${p.pace}`);
  if (p.notes) lines.push(`Otras notas: ${p.notes}`);
  return lines.join("\n");
}
